'use client';

import { useRef, useState } from 'react';

import { nameFromPlace } from '@/components/maps/place-view';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

import { LocationPicker, type ChosenPlace } from '@/components/maps/LocationPicker';
import { useLocale } from '@/lib/i18n';

const fieldCls =
  'w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none transition placeholder:text-stone-400 focus-visible:border-[#FF8A00] focus-visible:ring-2 focus-visible:ring-[#FF8A00]/20';
const labelCls = 'mb-1.5 block text-xs font-semibold text-stone-700';

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function DemoRequestForm() {
  const { t, lang } = useLocale();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [place, setPlace] = useState<ChosenPlace | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const filledCompany = useRef('');

  function choosePlace(next: ChosenPlace) {
    setPlace(next);
    const company = formRef.current?.elements.namedItem('company');
    if (!(company instanceof HTMLInputElement)) return;
    const incoming = nameFromPlace(company.value, filledCompany.current, next.name)?.slice(0, 120);
    if (incoming === undefined) return;
    company.value = incoming;
    filledCompany.current = incoming;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form).entries());
    if (place === null) {
      setError(t('landing.contact.place.missing'));
      return;
    }
    setStatus('sending');
    setError(null);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, locale: lang }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? t('landing.contact.error'));
      }
      form.reset();
      filledCompany.current = '';
      setPlace(null);
      setStatus('sent');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t('landing.contact.error'));
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
        <p className="mt-3 text-sm font-semibold text-emerald-900">{t('landing.contact.success')}</p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
      <div className="sm:col-span-1">
        <label className={labelCls} htmlFor="demo-name">
          {t('landing.contact.name')} *
        </label>
        <input id="demo-name" name="name" required minLength={2} maxLength={120} className={fieldCls} autoComplete="name" />
      </div>
      <div className="sm:col-span-1">
        <label className={labelCls} htmlFor="demo-email">
          {t('landing.contact.email')} *
        </label>
        <input id="demo-email" name="email" type="email" required maxLength={191} className={fieldCls} autoComplete="email" />
      </div>
      <div className="sm:col-span-1">
        <label className={labelCls} htmlFor="demo-phone">
          {t('landing.contact.phone')}
        </label>
        <input id="demo-phone" name="phone" maxLength={32} className={fieldCls} autoComplete="tel" />
      </div>
      <div className="sm:col-span-1">
        <label className={labelCls} htmlFor="demo-company">
          {t('landing.contact.company')}
        </label>
        <input id="demo-company" name="company" maxLength={120} className={fieldCls} autoComplete="organization" />
      </div>
      <div className="rounded-2xl border border-stone-200 bg-white p-3 sm:col-span-2 sm:p-4">
        <LocationPicker
          value={place}
          onChange={choosePlace}
          hint={t('landing.contact.place.hint')}
          lookingLabel={t('landing.contact.place.looking')}
          failedLabel={t('landing.contact.place.failed')}
        />
        <input type="hidden" id="demo-city" name="city" value={place?.city ?? ''} />
        <input type="hidden" name="address" value={place?.address ?? ''} />
        <input type="hidden" name="latitude" value={place?.latitude ?? ''} />
        <input type="hidden" name="longitude" value={place?.longitude ?? ''} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor="demo-message">
          {t('landing.contact.message')}
        </label>
        <textarea id="demo-message" name="message" rows={3} maxLength={2000} className={fieldCls} />
      </div>

      {/* Honeypot: hidden from people, tempting to bots. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {status === 'error' && error && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-red-600 sm:col-span-2">
          <AlertCircle size={14} />
          {error}
        </p>
      )}

      <div className="sm:col-span-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] text-stone-500">{t('landing.contact.required')}</p>
          <button
            type="submit"
            disabled={status === 'sending'}
            className="inline-flex items-center justify-center rounded-full bg-[#FF8A00] px-6 py-3 text-sm font-semibold text-stone-950 transition hover:bg-[#ea7900] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9a3412] disabled:opacity-60"
          >
            {status === 'sending' ? t('landing.contact.sending') : t('landing.contact.submit')}
          </button>
        </div>
      </div>
    </form>
  );
}
